import { computed, ref, toRaw, watch } from 'vue';

import { getCurrentInstance, paramCase } from '@components/utils';

import { useToggleScope } from './toggleScope';

import type { Ref } from 'vue';

type InnerVal<T> = T extends any[] ? Readonly<T> : T;

export function useProxyModel<
  Props extends object & { [key in Prop as `onUpdate:${Prop}`]?: ((val: any) => void) | undefined },
  Prop extends Extract<keyof Props, string>,
  Inner = Props[Prop]
>(
  props: Props,
  prop: Prop,
  defaultValue?: Props[Prop],
  transformIn: (value?: Props[Prop]) => Inner = (v: any) => v,
  transformOut: (value: Inner) => Props[Prop] = (v: any) => v
) {
  const vm = getCurrentInstance('useProxyModel');
  const internal = ref(props[prop] !== 'undefined' ? props[prop] : defaultValue) as Ref<Props[Prop]>;
  const kebabProp = paramCase(prop);
  const checkKebab = kebabProp !== prop;

  const isControlled = checkKebab
    ? computed(() => {
        void props[prop];
        return !!(
          (vm.vnode.props?.hasOwnProperty(prop) || vm.vnode.props?.hasOwnProperty(kebabProp)) &&
          (vm.vnode.props?.hasOwnProperty(`onUpdate:${prop}`) ||
            vm.vnode.props?.hasOwnProperty(`onUpdate:${kebabProp}`))
        );
      })
    : computed(() => {
        void props[prop];
        return !!(vm.vnode.props?.hasOwnProperty(prop) && vm.vnode.props?.hasOwnProperty(`onUpdate:${prop}`));
      });

  useToggleScope(
    () => !isControlled.value,
    () => {
      watch(
        () => props[prop],
        (value) => {
          internal.value = value;
        }
      );
    }
  );

  const model = computed({
    get(): any {
      const externalValue = props[prop];
      return transformIn(isControlled.value ? externalValue : internal.value);
    },
    set(internalValue) {
      const newValue = transformOut(internalValue);
      const value = toRaw(isControlled.value ? props[prop] : internal.value);

      if (value === newValue || transformIn(value) === internalValue) {
        return;
      }

      internal.value = newValue;
      vm?.emit(`update:${prop}`, newValue);
    }
  }) as any as Ref<InnerVal<Inner>> & { readonly externalValue: Props[Prop] };

  Object.defineProperty(model, 'externalValue', {
    get: () => (isControlled.value ? props[prop] : internal.value)
  });

  return model;
}
